/**
 * L1J Web Client - Identify Dialog
 *
 * 확인(감정) 주문서 결과 창 (화면 중앙, 기본 숨김).
 *
 * 서버 `S_IdentifyDesc`(opcode 43 = S_OPCODE_IDENTIFYDESC)는 완성된 문장을 보내지
 * 않는다. 보내는 것은 아이템 설명 ID와 **메시지 템플릿 번호 + 인자**뿐이다:
 *
 *   writeC(S_OPCODE_IDENTIFYDESC) + writeH(itemDescId)
 *   + writeH(messageId) + writeC(인자 개수) + writeS(인자) * 개수
 *
 * 예: 무기는 messageId 134("%0：작은 몬스터 타격%1 큰 몬스터 타격%2"), 방어구는 135,
 * 완드류는 137처럼 종류마다 템플릿이 다르고, 인자에는 `$227`(축복된) 같은 이름 ID가
 * 그대로 섞여 온다. 템플릿 치환과 이름 해석은 **호출부(GameScene)가 끝낸 뒤**
 * 완성된 줄들을 `lines`로 넘긴다 — 이 창은 받은 문장을 그리기만 하는 읽기 전용
 * 정보창이다.
 *
 * 구조·스타일은 HtmlDialog와 같은 규약을 따른다:
 *   - Phaser Container + setScrollFactor(0) + UI 카메라 전용
 *   - 자식에게도 setScrollFactor(0)을 직접 걸어야 클릭 히트테스트가 맞는다
 *   - 닫기는 X 버튼 / [닫기] 버튼 / ESC(GameScene이 hide()를 부른다) 세 경로
 */

import Phaser from 'phaser';

/** 본문 한 줄의 높이(px). 두 줄로 접히는 설명을 고려해 넉넉히 잡는다. */
const ROW_HEIGHT = 26;

/** 화면에 한 번에 보여줄 최대 줄 수. 넘으면 잘라내고 남은 수를 알린다. */
const MAX_ROWS = 7;

export class IdentifyDialog extends Phaser.GameObjects.Container {
  private panelWidth = 380;
  private panelHeight = 260;

  private titleText!: Phaser.GameObjects.Text;
  private rowTexts: Phaser.GameObjects.Text[] = [];
  private emptyText!: Phaser.GameObjects.Text;
  private overflowText!: Phaser.GameObjects.Text;

  /** 창이 닫힐 때 알린다 (GameScene이 ESC 처리 상태를 정리하는 데 쓴다). */
  public onClose: (() => void) | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);

    this.setScrollFactor(0);

    this.createUI();
    // 인벤토리(200)에서 주문서를 더블클릭해 열리므로 그보다 위여야 한다.
    // 대상 선택 배너(300)보다는 아래.
    this.setDepth(210);
    this.setVisible(false);
  }

  public getPanelWidth(): number {
    return this.panelWidth;
  }

  public getPanelHeight(): number {
    return this.panelHeight;
  }

  /** 창이 열려 있는지. ESC 처리 우선순위 판단에 쓴다. */
  public isOpen(): boolean {
    return this.visible;
  }

  private createUI(): void {
    const w = this.panelWidth;
    const h = this.panelHeight;

    const bg = this.scene.add.rectangle(0, 0, w, h, 0x1a1a2e, 0.96);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.add(bg);

    const titleBar = this.scene.add.rectangle(0, -h / 2 + 15, w, 30, 0x2a2a4e);
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.add(titleBar);

    this.titleText = this.scene.add.text(-w / 2 + 12, -h / 2 + 15, '감정 결과', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
      // 제목 자리는 X 버튼 앞까지다. 긴 아이템 이름이 버튼을 덮지 않도록 잘라 준다.
      fixedWidth: w - 48,
    });
    this.titleText.setOrigin(0, 0.5);
    this.add(this.titleText);

    const closeBtn = this.scene.add.text(w / 2 - 20, -h / 2 + 15, 'X', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ff6666',
    });
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    // 걸지 않으면 카메라가 스크롤된 상태에서 히트테스트가 월드 좌표로 계산돼 빗나간다.
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    closeBtn.on('pointerover', () => closeBtn.setStyle({ color: '#ffaaaa' }));
    closeBtn.on('pointerout', () => closeBtn.setStyle({ color: '#ff6666' }));
    // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다
    // (Phaser는 오브젝트 핸들러를 먼저 돌리고 씬 pointerdown을 나중에 쏜다).
    // hide() 자체에는 넣지 않는다 — ESC/코드로 닫힐 때는 클릭이 없다.
    closeBtn.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeBtn);

    // 본문 줄을 미리 만들어 두고 내용만 갈아 끼운다 (HtmlDialog와 같은 방식).
    const left = -w / 2 + 12;
    let y = -h / 2 + 50;

    for (let i = 0; i < MAX_ROWS; i++) {
      const row = this.scene.add.text(left, y, '', {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        wordWrap: { width: w - 24 },
      });
      row.setOrigin(0, 0);
      row.setVisible(false);
      this.add(row);
      this.rowTexts.push(row);
      y += ROW_HEIGHT;
    }

    this.emptyText = this.scene.add.text(0, 10, '(설명이 없는 아이템입니다)', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.emptyText.setOrigin(0.5);
    this.add(this.emptyText);

    this.overflowText = this.scene.add.text(0, h / 2 - 46, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.overflowText.setOrigin(0.5);
    this.add(this.overflowText);

    // 하단 닫기 버튼 — X는 작아서 놓치기 쉬우므로 본문 아래에도 둔다.
    const closeLabel = this.scene.add.text(0, h / 2 - 22, '[ 닫기 (ESC) ]', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    closeLabel.setOrigin(0.5);
    closeLabel.setInteractive({ useHandCursor: true });
    closeLabel.setScrollFactor(0);
    closeLabel.on('pointerover', () => closeLabel.setStyle({ color: '#ffd700' }));
    closeLabel.on('pointerout', () => closeLabel.setStyle({ color: '#ffffff' }));
    closeLabel.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeLabel);
  }

  /**
   * 감정 결과를 채우고 창을 연다.
   *
   * @param title 감정한 아이템 이름. 빈 문자열이면 제목만 남는다.
   * @param lines 이미 완성된 설명 줄들 (`$숫자` 치환은 호출부가 끝냈다고 본다).
   */
  public show(title: string, lines: string[]): void {
    this.titleText.setText(title ? `감정 결과 - ${title}` : '감정 결과');

    const rows = (lines ?? []).map((line) => (line ?? '').trim()).filter((line) => line.length > 0);

    this.emptyText.setVisible(rows.length === 0);

    this.rowTexts.forEach((row, index) => {
      const line = rows[index];
      if (line === undefined) {
        row.setVisible(false);
        return;
      }
      row.setText(`· ${line}`);
      row.setVisible(true);
    });

    const overflow = rows.length - MAX_ROWS;
    this.overflowText.setText(overflow > 0 ? `... 외 ${overflow}줄` : '');

    this.setVisible(true);
  }

  /** 창을 닫는다. 닫혀 있으면 아무 일도 하지 않는다. */
  public hide(): void {
    if (!this.visible) return;
    this.setVisible(false);
    this.onClose?.();
  }
}
